import { UM } from '../_components';
import type { ChangeLogEntry, QualityStatus } from './types';

// ─── Cell renderers ──────────────────────────────────────────────────────────

function QualityDot({ status }: { status: QualityStatus }) {
  const color =
    status === 'validated' ? UM.green : status === 'conflict' ? UM.orange : UM.slate;
  return (
    <span
      style={{
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: color,
        marginRight: 6,
        verticalAlign: 'middle',
        flexShrink: 0,
      }}
    />
  );
}

function TypeBadge({ type }: { type: string }) {
  const isImport = type === 'Import';
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 500,
        padding: '2px 8px',
        borderRadius: 12,
        background: isImport ? UM.badgeBlue : UM.bgHover,
        color: isImport ? UM.badgeBlueText : '#475569',
        whiteSpace: 'nowrap',
      }}
    >
      {type}
    </span>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

interface ChangeLogTableProps {
  entries: ChangeLogEntry[];
}

export function ChangeLogTable({ entries }: ChangeLogTableProps) {
  return (
    <div style={{ flex: 1, overflowY: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '20%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '10%' }} />
        </colgroup>
        <thead>
          <tr style={{ background: UM.bgHeader, position: 'sticky', top: 0, zIndex: 1 }}>
            <th style={thStyle}>Attribut modifié</th>
            <th style={thStyle}>Nouvelle valeur</th>
            <th style={thStyle}>Auteur</th>
            <th style={thStyle}>Ancienne valeur</th>
            <th style={thStyle}>Date &amp; Heure</th>
            <th style={thStyle}>Type</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id} style={{ borderBottom: `1px solid ${UM.border}` }}>
              <td style={tdStyle}>{entry.attribute}</td>
              <td style={tdStyle}>
                <QualityDot status={entry.newValueStatus} />
                <strong>{entry.newValue}</strong>
              </td>
              <td style={tdStyle}>{entry.author}</td>
              <td style={tdStyle}>
                <QualityDot status={entry.oldValueStatus} />
                <span style={{ color: UM.textMuted }}>{entry.oldValue}</span>
              </td>
              <td style={{ ...tdStyle, color: UM.textMuted }}>{entry.dateTime}</td>
              <td style={tdStyle}>
                <TypeBadge type={entry.type} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: 11,
  fontWeight: 600,
  color: '#475569',
  borderBottom: `2px solid ${UM.border}`,
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  verticalAlign: 'middle',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
